import React, { useEffect, useState } from "react";
import { Ticket, Loader2, Crown } from "lucide-react";
import { PageHeader } from "@/components/shell/TopBar";
import { Paywall } from "@/components/launch/Paywall";
import { fetchCoupon, postLaunchEvent } from "@/api/launch";
import { useI18n } from "@/i18n";

function asArray(d: any): any[] {
  if (Array.isArray(d)) return d;
  return d?.coupons ?? d?.items ?? d?.data ?? [];
}
function legsOf(c: any): any[] {
  return c?.legs ?? c?.selections ?? c?.matches ?? c?.picks ?? [];
}
const S = (v: unknown) => (v == null || v === "" ? null : String(v));

export default function CouponCenter() {
  const { lang } = useI18n();
  const [state, setState] = useState<"loading" | "ok" | "locked" | "error">("loading");
  const [coupons, setCoupons] = useState<any[]>([]);
  const [raw, setRaw] = useState<any>(null);

  useEffect(() => {
    postLaunchEvent("coupon_viewed");
    fetchCoupon()
      .then((d) => { setRaw(d); setCoupons(asArray(d)); setState("ok"); })
      .catch((e: any) => { setState(e?.status === 401 || e?.status === 403 ? "locked" : "error"); });
  }, []);

  return (
    <div data-testid="coupon-center-page">
      <PageHeader title={lang === "tr" ? "Kupon Merkezi" : "Coupon Center"}
        subtitle={lang === "tr" ? "PREMIUM · ATLAS Kupon Merkezi" : "PREMIUM · ATLAS Coupon Center"} />

      <div className="mb-5 flex items-center gap-2 text-sm text-gold-300">
        <Crown className="h-4 w-4" />
        {lang === "tr" ? "Production motorunun doğruladığı seçimlerden oluşan AI kuponları." : "AI coupons built from selections validated by the production engine."}
      </div>

      {state === "loading" && <div className="grid place-items-center py-16"><Loader2 className="h-6 w-6 animate-spin text-atlas-400" /></div>}

      {state === "locked" && (
        <Paywall feature="coupon_center"
          title={lang === "tr" ? "PREMIUM · ATLAS Kupon Merkezi" : "PREMIUM · ATLAS Coupon Center"}
          body={lang === "tr" ? "AI kuponları PREMIUM plana dahildir. 3 günlük ELITE denemesi 1 demo kupon gösterir." : "AI coupons are part of PREMIUM. The 3-day ELITE trial shows 1 demo coupon."} />
      )}

      {state === "error" && (
        <div className="rounded-2xl border border-white/8 bg-white/[0.02] p-6 text-center text-sm text-muted-foreground">
          {lang === "tr" ? "Kuponlar şu anda alınamadı. Lütfen daha sonra tekrar dene." : "Coupons are unavailable right now. Please try again later."}
        </div>
      )}

      {state === "ok" && coupons.length === 0 && (
        <div className="rounded-2xl border border-white/8 bg-white/[0.02] p-6 text-center text-sm text-muted-foreground" data-testid="coupon-empty">
          {lang === "tr" ? "ATLAS şu anda yayın kriterini geçen bir kupon üretmedi." : "ATLAS has not produced a coupon meeting the publishing criteria right now."}
        </div>
      )}

      {state === "ok" && coupons.length > 0 && (
        <div className="grid gap-4 lg:grid-cols-2">
          {coupons.map((c, i) => {
            const legs = legsOf(c);
            const title = S(c?.title) ?? S(c?.name) ?? (lang === "tr" ? `Kupon #${i + 1}` : `Coupon #${i + 1}`);
            const conf = S(c?.confidence) ?? S(c?.confidence_label);
            const totalOdds = S(c?.total_odds) ?? S(c?.odds) ?? S(c?.combined_odds);
            return (
              <div key={c?.id ?? i} className="panel p-5" data-testid={`coupon-card-${i}`}>
                <div className="flex items-center justify-between">
                  <h3 className="inline-flex items-center gap-2 text-base font-bold text-foreground"><Ticket className="h-4 w-4 text-gold-400" /> {title}</h3>
                  {totalOdds && <span className="num rounded-full border border-gold/25 bg-gold/10 px-2.5 py-0.5 text-xs font-bold text-gold-300">{totalOdds}</span>}
                </div>
                {conf && <p className="mt-1 text-xs text-muted-foreground">{lang === "tr" ? "Güven" : "Confidence"}: {conf}</p>}
                {legs.length > 0 ? (
                  <ul className="mt-3 space-y-2">
                    {legs.map((l: any, j: number) => {
                      const match = S(l?.match) ?? S(l?.fixture) ?? [S(l?.home), S(l?.away)].filter(Boolean).join(" — ");
                      const market = S(l?.market) ?? S(l?.market_label);
                      const pick = S(l?.selection) ?? S(l?.pick) ?? S(l?.recommendation);
                      const odd = S(l?.odd) ?? S(l?.odds);
                      return (
                        <li key={j} className="rounded-xl border border-white/5 bg-white/[0.02] p-3 text-sm">
                          {match && <p className="font-semibold text-foreground">{match}</p>}
                          <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
                            {market && <span>{market}</span>}
                            {pick && <span className="text-atlas-300">{pick}</span>}
                            {odd && <span className="num">{odd}</span>}
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                ) : (
                  <p className="mt-3 text-sm text-muted-foreground">{lang === "tr" ? "Bu kuponun seçim ayrıntısı mevcut değil." : "No selection detail available for this coupon."}</p>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
